import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonProps,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid2 as Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  resolveSurveyStatus,
  useDeleteSurveyMutation,
  useDuplicateSurveyMutation,
  useFetchSurveyQuery,
  useFetchSurveyResponsesQuery,
  useUpdateSurveyStatusMutation,
} from "@/api/surveysApi";
import { useAppSelector, useResponsive } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import PageHeader from "@/components/PageHeader";
import ButtonGroup from "@/components/ButtonGroup";
import Frame from "@/components/Frame";
import { blue, orange } from "@mui/material/colors";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Activity01Icon,
  Chatting01Icon,
  Copy01Icon,
  PauseCircleIcon,
  SparklesIcon,
  SquareLock01Icon,
  StarIcon,
} from "@hugeicons/core-free-icons";
import SurveyShareDialog from "./components/SurveyShareDialog";
import SurveyGoogleReviewsDialog from "./components/SurveyGoogleReviewsDialog";
import SurveyAIAnalysisDialog from "./components/SurveyAIAnalysisDialog";
import SurveyResponsesList from "./components/SurveyResponsesList";
import {
  Question,
  QuestionStats,
  SurveyRecentComment,
  SurveyResponse,
  SurveyStatus,
} from "@/types/surveys";

const STATUS_CHIP_CONFIG: Record<
  SurveyStatus,
  {
    label: string;
    color: "success" | "warning" | "default" | "error";
  }
> = {
  active: { label: "Activa", color: "success" },
  draft: { label: "Borrador", color: "default" },
  paused: { label: "Pausada", color: "warning" },
  finished: { label: "Finalizada", color: "error" },
};

const EMPTY_STATS: Record<string, QuestionStats> = {};
const EMPTY_COMMENTS: SurveyRecentComment[] = [];

const aggregateResponsesFallback = (
  questions: Question[],
  responses: SurveyResponse[]
): {
  stats: Record<string, QuestionStats>;
  recentComments: SurveyRecentComment[];
} => {
  const stats: Record<string, QuestionStats> = {};
  const recentComments: SurveyRecentComment[] = [];

  for (const resp of responses) {
    const rawAnswers = resp.answers as any;
    if (!rawAnswers) continue;

    // Extraer rating principal si existe en la respuesta
    let primaryRating: number | undefined;
    for (const q of questions) {
      if (q.type === "rating") {
        const val = Array.isArray(rawAnswers)
          ? rawAnswers.find((a: any) => a.question === q.title)?.answer
          : rawAnswers[q.id];
        const num = Number(val);
        if (!Number.isNaN(num) && num >= 1 && num <= 5) {
          primaryRating = num;
          break;
        }
      }
    }

    questions.forEach((question, qIdx) => {
      const qKey = question.id || String(qIdx);
      const qType = question.type || "single_choice";

      let rawAnswer: any;
      if (Array.isArray(rawAnswers)) {
        const match = rawAnswers.find((a: any) => a.question === question.title);
        rawAnswer = match?.answer;
      } else {
        rawAnswer = rawAnswers[qKey] ?? rawAnswers[question.title];
      }

      if (rawAnswer === undefined || rawAnswer === null || rawAnswer === "") {
        return;
      }

      const currentStat: QuestionStats = stats[qKey] ?? { total: 0 };

      if (qType === "rating") {
        const numVal = Number(rawAnswer);
        if (!Number.isNaN(numVal) && numVal >= 1 && numVal <= 5) {
          const dist = { ...(currentStat.distribution ?? {}) };
          const starKey = String(Math.round(numVal));
          dist[starKey] = (dist[starKey] ?? 0) + 1;
          stats[qKey] = {
            ...currentStat,
            total: (currentStat.total ?? 0) + 1,
            sum: (currentStat.sum ?? 0) + numVal,
            distribution: dist,
          };
        }
      } else if (qType === "single_choice") {
        const opts = { ...(currentStat.options ?? {}) };
        let optKey = String(rawAnswer);
        // Si es respuesta legacy guardada por título en vez de id, mapear al id de la opción
        const matchedOpt = (question.options ?? []).find(
          (o, idx) => (o.id || String(idx)) === optKey || o.title === optKey
        );
        if (matchedOpt) {
          const idx = (question.options ?? []).indexOf(matchedOpt);
          optKey = matchedOpt.id || String(idx);
        }
        opts[optKey] = (opts[optKey] ?? 0) + 1;
        stats[qKey] = {
          ...currentStat,
          total: (currentStat.total ?? 0) + 1,
          options: opts,
        };
      } else if (qType === "multiple_choice") {
        const selected = Array.isArray(rawAnswer) ? rawAnswer : [rawAnswer];
        if (selected.length > 0) {
          const opts = { ...(currentStat.options ?? {}) };
          for (const item of selected) {
            const key = String(item);
            opts[key] = (opts[key] ?? 0) + 1;
          }
          stats[qKey] = {
            ...currentStat,
            total: (currentStat.total ?? 0) + 1,
            options: opts,
          };
        }
      } else if (qType === "text") {
        const textVal = String(rawAnswer).trim();
        if (textVal.length > 0) {
          stats[qKey] = {
            ...currentStat,
            total: (currentStat.total ?? 0) + 1,
          };
          recentComments.push({
            id: `${resp.id}_${qKey}`,
            questionId: qKey,
            text: textVal.slice(0, 500),
            rating: primaryRating,
            orderId: resp.orderId ?? null,
            orderNumber: resp.orderNumber ?? null,
            branchId: resp.branchId ?? null,
            createdAt:
              typeof resp.createdAt === "number" ? resp.createdAt : Date.now(),
          });
        }
      }
    });
  }

  return { stats, recentComments };
};

const SurveyDetailsPage = () => {
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const { isMobile } = useResponsive();
  const container = useAppSelector((state) => state.ui?.container);
  const { organizationId, surveyId } = useParams<{
    organizationId: string;
    surveyId: string;
  }>();
  const organization = useAppSelector((state) =>
    organizationId ? state.organizations[organizationId] : null
  );

  const [shareOpen, setShareOpen] = useState(false);
  const [googleReviewsOpen, setGoogleReviewsOpen] = useState(false);
  const [selectedResponseAnswers, setSelectedResponseAnswers] = useState<
    Record<string, string | string[] | number> | undefined
  >(undefined);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [hasOpenedResponsesTab, setHasOpenedResponsesTab] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      setHasOpenedResponsesTab(true);
    }
  };

  const surveyQuery = useFetchSurveyQuery(
    organizationId && surveyId ? { organizationId, surveyId } : skipToken
  );

  const responsesQuery = useFetchSurveyResponsesQuery(
    {
      organizationId: organizationId || "",
      surveyId: surveyId || "",
      maxLimit: 25,
    },
    { skip: !organizationId || !surveyId || !hasOpenedResponsesTab }
  );

  const [updateSurveyStatus, statusMutationState] = useUpdateSurveyStatusMutation();
  const [duplicateSurvey, duplicateState] = useDuplicateSurveyMutation();
  const [deleteSurvey, deleteState] = useDeleteSurveyMutation();

  const survey = surveyQuery.data;
  const responsesList = responsesQuery.data ?? [];

  const orderNumberByOrderId = useMemo(() => {
    const map = new Map<string, string | number>();
    for (const r of responsesList) {
      if (r.orderId && r.orderNumber !== undefined && r.orderNumber !== null && r.orderNumber !== "") {
        map.set(r.orderId, r.orderNumber);
      }
    }
    return map;
  }, [responsesList]);

  const computedFallback = useMemo(() => {
    if (!survey?.questions?.length || responsesList.length === 0) {
      return null;
    }
    return aggregateResponsesFallback(survey.questions, responsesList);
  }, [survey?.questions, responsesList]);

  if (surveyQuery.isLoading) {
    return (
      <Stack flex={1} alignItems="center" py={5}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!survey) {
    return <Navigate to="/not-found" replace={true} />;
  }

  const preAggregatedTotal = survey.totalResponses ?? survey.answers ?? 0;
  const hasPreAggregatedStats =
    preAggregatedTotal >= responsesList.length &&
    Object.keys(survey.stats ?? {}).length > 0;

  const totalResponses = Math.max(preAggregatedTotal, responsesList.length);
  const isPostOrder = survey.trigger === "post_order";
  const currentStatus = resolveSurveyStatus(survey);
  const statusChip = STATUS_CHIP_CONFIG[currentStatus] ?? STATUS_CHIP_CONFIG.draft;
  const isLocked = Boolean(
    currentStatus !== "draft" || survey.isPublished || totalResponses > 0 || survey.hasResponses
  );
  const stats = hasPreAggregatedStats
    ? (survey.stats ?? EMPTY_STATS)
    : (computedFallback?.stats ?? survey.stats ?? EMPTY_STATS);
  const recentComments = hasPreAggregatedStats
    ? (survey.recentComments ?? EMPTY_COMMENTS)
    : (computedFallback?.recentComments ?? survey.recentComments ?? EMPTY_COMMENTS);

  // Calcular promedio general si hay preguntas tipo rating
  const ratingQuestions = (survey.questions ?? []).filter((q) => q.type === "rating");
  let overallRatingAvg: number | null = null;
  if (ratingQuestions.length > 0) {
    let sumAll = 0;
    let countAll = 0;
    for (const rq of ratingQuestions) {
      const qStat = stats[rq.id];
      if (qStat && qStat.total > 0 && typeof qStat.sum === "number") {
        sumAll += qStat.sum;
        countAll += qStat.total;
      }
    }
    if (countAll > 0) {
      overallRatingAvg = Number((sumAll / countAll).toFixed(1));
    }
  }

  const publicSurveyUrl = organization?.alias
    ? `/${organization.alias}/surveys/${survey.id}`
    : `/comercios/${organizationId}/surveys/${survey.id}/submit`;

  const handleStatusChange = async (nextStatus: SurveyStatus) => {
    if (!organizationId || !surveyId || nextStatus === currentStatus) return;
    if (isPostOrder && (nextStatus === "finished" || nextStatus === "draft")) return;

    if (nextStatus === "active" && (!survey.questions || survey.questions.length === 0)) {
      openSnackbar("Agrega al menos una pregunta antes de activar la encuesta", "warning");
      return;
    }

    try {
      await updateSurveyStatus({
        organizationId,
        surveyId,
        status: nextStatus,
      }).unwrap();

      const statusMessages: Record<SurveyStatus, string> = {
        active: "Encuesta activada exitosamente",
        paused: "Encuesta pausada temporalmente",
        finished: "Encuesta marcada como finalizada",
        draft: "Encuesta guardada como borrador",
      };
      openSnackbar(statusMessages[nextStatus], "success");
    } catch {
      openSnackbar("Error al cambiar el estado de la encuesta", "error");
    }
  };

  const handleDelete = async () => {
    if (!organizationId || !surveyId || isPostOrder) return;
    if (!window.confirm(`¿Deseas eliminar la encuesta "${survey.title}"?`)) return;

    try {
      await deleteSurvey({
        organizationId,
        surveyId,
      }).unwrap();
      openSnackbar("Encuesta eliminada", "success");
      navigate(`/comercios/${organizationId}/surveys`);
    } catch {
      openSnackbar("Error al eliminar la encuesta", "error");
    }
  };

  const handleDuplicate = async () => {
    if (!organizationId || isPostOrder) return;
    try {
      const result = await duplicateSurvey({
        organizationId,
        survey,
      }).unwrap();
      openSnackbar("Encuesta duplicada como borrador para edición", "success");
      navigate(`/comercios/${organizationId}/surveys/${result.id}/edit`);
    } catch {
      openSnackbar("Error al duplicar la encuesta", "error");
    }
  };

  const renderQuestionStats = (question: Question, index: number) => {
    const qStat = stats[question.id];
    const qTotal = qStat?.total ?? 0;
    const qType = question.type || "single_choice";

    return (
      <Box key={question.id || `q-${index}`}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h4">
              {index + 1}. {question.title}
            </Typography>
            <Chip
              size="small"
              label={qTotal}
            />
          </Stack>

          {qType === "rating" ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h2" fontWeight={700}>
                  {qTotal > 0 && qStat?.sum
                    ? (qStat.sum / qTotal).toFixed(1)
                    : "0.0"}
                </Typography>
                <Stack>
                  <Rating
                    readOnly
                    precision={0.1}
                    value={qTotal > 0 && qStat?.sum ? qStat.sum / qTotal : 0}
                  />
                  <Typography variant="caption">
                    Basado en {qTotal} calificaciones
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                {[5, 4, 3, 2, 1].map((star) => {
                  const starCount = qStat?.distribution?.[String(star)] ?? 0;
                  const pct = qTotal > 0 ? Math.round((starCount / qTotal) * 100) : 0;
                  return (
                    <Stack key={star} direction="row" spacing={2} alignItems="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ minWidth: 55 }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {star}
                        </Typography>
                        <HugeiconsIcon icon={StarIcon} size={14} color="#faaf00" />
                      </Stack>
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 75, textAlign: "right" }}
                      >
                        {starCount} ({pct}%)
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          ) : qType === "text" ? (
            <Stack spacing={1}>
              {recentComments.filter((c) => c.questionId === question.id).length ===
                0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no hay comentarios de texto registrados para esta pregunta.
                </Typography>
              ) : (
                recentComments
                  .filter((c) => c.questionId === question.id)
                  .map((comment) => (
                    <Frame
                      key={comment.id}
                      deep={1}
                      sx={{
                        backgroundColor: "#F8FAFC",
                        border: "none",
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography variant="body1">"{comment.text}"</Typography>
                    </Frame>
                  ))
              )}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {(question.options ?? []).map((option, optionIndex) => {
                const optionKey = option.id || String(optionIndex);
                const count = qStat?.options?.[optionKey] ?? 0;
                const pct = qTotal > 0 ? Math.round((count / qTotal) * 100) : 0;

                return (
                  <Stack key={optionKey} spacing={0.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body1">{option.title}</Typography>
                      <Typography variant="body2">
                        {count} votos ({pct}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  const headerActions: ButtonProps[] = [
    {
      children: "Compartir / QR",
      color: "success",
      onClick: () => setShareOpen(true),
    },
    {
      children: "Vista Cliente",
      onClick: () => window.open(publicSurveyUrl, "_blank", "noopener,noreferrer"),
    },
    ...(isPostOrder || !isLocked
      ? [
        {
          children: "Editar Preguntas",
          onClick: () => navigate("edit"),
        },
      ]
      : []),
    ...(!isPostOrder && !isLocked
      ? [
        {
          children: "Duplicar encuesta",
          disabled: duplicateState.isLoading,
          onClick: handleDuplicate,
        },
      ]
      : []),
    ...(!isPostOrder
      ? [
        {
          children: "Eliminar encuesta",
          color: "error" as const,
          disabled: deleteState.isLoading,
          onClick: handleDelete,
        },
      ]
      : []),
  ];

  const hasLinkedPlaceId = Boolean(organization?.googlePlaceId?.trim());

  return (
    <Stack spacing={3}>
      <PageHeader
        title={survey.title}
        action={<ButtonGroup actions={headerActions} />}
      />

      <Stack flex={1}>
        <Container
          maxWidth={container === "fixed" ? "lg" : false}
          sx={{ pb: 5 }}
        >
          <Grid container spacing={3} alignItems="flex-start">
            {/* Columna izquierda principal (8 columnas) */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                {/* Resumen KPI superior con layout estilo OrderSummary */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card>
                      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack>
                            <Typography
                              variant="body1"
                              lineHeight={1.125}
                              component="span"
                              fontSize="2.5rem"
                              color="primary.main"
                            >
                              {totalResponses}
                            </Typography>
                            <Typography
                              variant="overline"
                              lineHeight={1.125}
                              color="textSecondary"
                            >
                              TOTAL DE RESPUESTAS
                            </Typography>
                          </Stack>
                          <Avatar sx={{ backgroundColor: blue[500], padding: 1.5 }}>
                            <HugeiconsIcon icon={Chatting01Icon} size={32} color="white" />
                          </Avatar>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Card>
                      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Stack>
                            <Typography
                              variant="body1"
                              lineHeight={1.125}
                              component="span"
                              fontSize="2.5rem"
                              color="primary.main"
                            >
                              {overallRatingAvg !== null ? `${overallRatingAvg}` : "—"}
                            </Typography>
                            <Typography
                              variant="overline"
                              lineHeight={1.125}
                              color="textSecondary"
                            >
                              CALIFICACIÓN PROMEDIO
                            </Typography>
                          </Stack>
                          <Avatar sx={{ backgroundColor: orange[500], padding: 1.5 }}>
                            <HugeiconsIcon icon={StarIcon} size={32} color="white" />
                          </Avatar>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {currentStatus === "paused" && (
                  <Alert
                    icon={<HugeiconsIcon icon={PauseCircleIcon} size={18} />}
                    severity="warning"
                    sx={{ borderRadius: 2 }}
                    action={
                      !isMobile ? (
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => handleStatusChange("active")}
                          disabled={statusMutationState.isLoading}
                        >
                          Reanudar encuesta
                        </Button>
                      ) : undefined
                    }
                  >
                    Esta encuesta se encuentra <strong>pausada</strong>. Los usuarios que ingresen
                    al enlace verán un aviso indicando que por el momento la encuesta no puede ser
                    llenada.
                  </Alert>
                )}

                {currentStatus === "finished" && (
                  <Alert
                    severity="error"
                    sx={{ borderRadius: 2 }}
                    action={
                      !isMobile ? (
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => handleStatusChange("active")}
                          disabled={statusMutationState.isLoading}
                        >
                          Reactivar encuesta
                        </Button>
                      ) : undefined
                    }
                  >
                    Esta encuesta se encuentra <strong>finalizada</strong>. Los usuarios que
                    ingresen al enlace verán un aviso indicando que la encuesta ha concluido y ya
                    no puede ser llenada.
                  </Alert>
                )}

                {/* Desglose de preguntas y respuestas individuales con pestañas */}
                <Paper sx={{ borderRadius: 2 }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant={isMobile ? "scrollable" : "standard"}
                      aria-label="secciones de encuesta"
                    >
                      <Tab label="Resumen de Preguntas" />
                      <Tab
                        label={
                          totalResponses > 0 ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <span>Respuestas Individuales</span>
                              <Chip
                                size="small"
                                label={totalResponses}
                                sx={{ height: 20, fontSize: "0.75rem", pointerEvents: "none" }}
                              />
                            </Stack>
                          ) : (
                            "Respuestas Individuales"
                          )
                        }
                      />
                    </Tabs>
                  </Box>
                  <Stack spacing={3} p={3}>

                    {activeTab === 0 && (
                      <Stack spacing={3} divider={<Divider />}>
                        {(survey.questions ?? []).length > 0 ? (
                          (survey.questions ?? []).map((question, idx) =>
                            renderQuestionStats(question, idx)
                          )
                        ) : (
                          <Box sx={{ py: 4, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              Esta encuesta no tiene preguntas configuradas.
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    )}

                    {activeTab === 1 && (
                      <SurveyResponsesList
                        responses={responsesList}
                        questions={survey.questions}
                        organizationId={organizationId || ""}
                        orderNumberByOrderId={orderNumberByOrderId}
                        isLoading={responsesQuery.isLoading || responsesQuery.isFetching}
                      />
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid>

            {/* Columna derecha (4 columnas) con estructura unificada en todas las cajas */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3}>
                {/* 1. Caja de Estado de la Encuesta / Cambiar estado / Pausar / Finalizar */}
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1.5}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "rgba(1, 95, 238, 0.1)",
                            color: "#015FEE",
                          }}
                        >
                          <HugeiconsIcon icon={Activity01Icon} size={20} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700}>
                          Estado de la Encuesta
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        color={statusChip.color}
                        label={statusChip.label}
                      />
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      {isPostOrder
                        ? "Controla si la encuesta Post-Pedido está activa o en pausa temporal."
                        : "Controla si la encuesta está disponible para recibir respuestas, en pausa o finalizada."}
                    </Typography>

                    <FormControl fullWidth size="small">
                      <Select
                        value={
                          isPostOrder && (currentStatus === "draft" || currentStatus === "finished")
                            ? "active"
                            : currentStatus
                        }
                        disabled={statusMutationState.isLoading}
                        onChange={(e) =>
                          handleStatusChange(e.target.value as SurveyStatus)
                        }
                      >
                        {!isPostOrder && <MenuItem value="draft">Borrador</MenuItem>}
                        <MenuItem value="active">Activa</MenuItem>
                        <MenuItem value="paused">Pausada</MenuItem>
                        {!isPostOrder && <MenuItem value="finished">Finalizada</MenuItem>}
                      </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {currentStatus !== "active" && (
                        <Button
                          fullWidth={isPostOrder || currentStatus === "draft"}
                          sx={{ flex: 1 }}
                          color="success"
                          variant="contained"
                          disabled={statusMutationState.isLoading}
                          onClick={() => handleStatusChange("active")}
                        >
                          {currentStatus === "paused" ? "Reanudar" : "Activar"}
                        </Button>
                      )}
                      {currentStatus === "active" && (
                        <Button
                          sx={{ flex: 1 }}
                          color="warning"
                          variant="outlined"
                          disabled={statusMutationState.isLoading}
                          onClick={() => handleStatusChange("paused")}
                        >
                          Pausar
                        </Button>
                      )}
                      {!isPostOrder &&
                        currentStatus !== "finished" &&
                        currentStatus !== "draft" && (
                          <Button
                            sx={{ flex: 1 }}
                            color="error"
                            variant="outlined"
                            disabled={statusMutationState.isLoading}
                            onClick={() => handleStatusChange("finished")}
                          >
                            Finalizar
                          </Button>
                        )}
                    </Stack>
                  </Stack>
                </Paper>

                {/* 2. Caja de Análisis Inteligente de Respuestas */}
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: "rgba(1, 95, 238, 0.1)",
                          color: "#015FEE",
                        }}
                      >
                        <HugeiconsIcon icon={SparklesIcon} size={20} />
                      </Avatar>
                      <Typography variant="h4" fontWeight={700}>
                        Análisis de Respuestas
                      </Typography>
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      Descubre en palabras simples qué están pensando tus clientes según sus
                      respuestas.
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => setAiDialogOpen(true)}
                      disabled={totalResponses === 0}
                      startIcon={<HugeiconsIcon icon={SparklesIcon} size={18} />}
                    >
                      Analizar respuestas
                    </Button>
                  </Stack>
                </Paper>

                {/* 3. Caja de Redirigir a Google Reviews */}
                {ratingQuestions.length > 0 && (
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1.5}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "10px",
                              bgcolor: "rgba(245, 158, 11, 0.12)",
                              color: "#d97706",
                            }}
                          >
                            <HugeiconsIcon icon={StarIcon} size={20} color="#d97706" />
                          </Avatar>
                          <Typography variant="h4" fontWeight={700}>
                            Google Reviews
                          </Typography>
                        </Stack>
                        {hasLinkedPlaceId && (
                          <Chip size="small" color="success" label="Vinculado" />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        Vincula el Place ID de tu comercio y configura el asistente de copiado de
                        puntaje y comentario hacia Google Reviews.
                      </Typography>

                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        startIcon={<HugeiconsIcon icon={StarIcon} size={18} />}
                        onClick={() => {
                          setSelectedResponseAnswers(undefined);
                          setGoogleReviewsOpen(true);
                        }}
                      >
                        Redirigir a Google Reviews
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {/* 4. Caja de Preguntas Bloqueadas / Duplicar como borrador */}
                {isLocked && !isPostOrder && (
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: "rgba(245, 158, 11, 0.12)",
                            color: "#d97706",
                          }}
                        >
                          <HugeiconsIcon icon={SquareLock01Icon} size={20} />
                        </Avatar>
                        <Typography variant="h4" fontWeight={700}>
                          Preguntas Bloqueadas
                        </Typography>
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        Las preguntas y opciones están bloqueadas porque esta encuesta ya fue
                        publicada o cuenta con respuestas registradas, garantizando la consistencia
                        de las estadísticas.
                      </Typography>

                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<HugeiconsIcon icon={Copy01Icon} size={18} />}
                        onClick={handleDuplicate}
                        disabled={duplicateState.isLoading}
                      >
                        Duplicar como borrador
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Stack>

      <SurveyShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        survey={survey}
        organizationAlias={organization?.alias}
        organizationId={organizationId}
      />

      <SurveyAIAnalysisDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        survey={survey}
        totalResponses={totalResponses}
        stats={stats}
        recentComments={recentComments}
      />

      <SurveyGoogleReviewsDialog
        open={googleReviewsOpen}
        onClose={() => {
          setGoogleReviewsOpen(false);
          setSelectedResponseAnswers(undefined);
        }}
        survey={survey}
        organizationId={organizationId}
        currentAnswers={selectedResponseAnswers}
      />
    </Stack>
  );
};

export default SurveyDetailsPage;
